import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, finalize, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../../common/services/notification/notification.service';
import { HubLoanerPhoneListItem } from '../../models/hub-loaner-phones.model';
import {
  HubLoanerPhoneListInitialState,
  HubLoanerPhoneListItemDelete,
  HubLoanerPhoneListItemReturn,
  HubLoanerPhoneListLoad,
} from './hub-loaner-phone-list.actions';
import { HubLoanerPhoneListService } from './hub-loaner-phone-list.service';
import { Error, CustomHttpErrorResponse } from '../../../../../../common/constants/error';
import { LIST_SERVER_PAGINATION_INITIAL_STATE } from '../../../../../../common/models/list.model';
import { ListServerPaginationStateModel } from '../../../../../../common';
import { HubLoanerPhoneItemStateModel } from './hub-loaner-phone-item/hub-loaner-phone-item.state';
import { HubLoanerPhoneItemService } from './hub-loaner-phone-item/hub-loaner-phone-item.service';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export type HubLoanerPhoneListStateModel = ListServerPaginationStateModel<HubLoanerPhoneListItem> & {
  isDeleting: boolean;
};

const initialState: HubLoanerPhoneListStateModel = { ...LIST_SERVER_PAGINATION_INITIAL_STATE, isDeleting: false };

@State<HubLoanerPhoneListStateModel>({
  name: 'hubLoanerPhoneList',
  defaults: initialState,
})
@Injectable()
export class HubLoanerPhoneListState {
  constructor(
    private hubLoanerPhoneListService: HubLoanerPhoneListService,
    private notificationService: NotificationService,
    private hubLoanerPhoneItemService: HubLoanerPhoneItemService,
  ) {}

  @Selector()
  static getState(state: HubLoanerPhoneListStateModel): HubLoanerPhoneListStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: HubLoanerPhoneListStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static isDeleting(state: HubLoanerPhoneListStateModel): boolean {
    return state.isDeleting;
  }

  @Action(HubLoanerPhoneListLoad)
  getHubLoanerPhoneList(
    ctx: StateContext<HubLoanerPhoneListStateModel>,
    { payload }: HubLoanerPhoneListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.hubLoanerPhoneListService.getLoanerPhoneList(payload).pipe(
      take(1),
      map((loanerPhones: HubLoanerPhoneListItem[]) => {
        ctx.patchState({ ...loanerPhones, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(HubLoanerPhoneListItemReturn)
  returnLoanerPhoneListItem(
    ctx: StateContext<HubLoanerPhoneItemStateModel>,
    { payload: { dialog, hubId, id, returnTimestamp } }: HubLoanerPhoneListItemReturn,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.hubLoanerPhoneItemService.returnLoanerPhoneItem(hubId, id, returnTimestamp).pipe(
      map(() => {
        dialog.close();
        this.notificationService.success('api.loanerPhone.success.return');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(HubLoanerPhoneListItemDelete)
  deleteLoanerPhoneListItem(
    ctx: StateContext<HubLoanerPhoneListStateModel>,
    { payload: { dialog, hubId, id } }: HubLoanerPhoneListItemDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    ctx.patchState({ isDeleting: true });

    return this.hubLoanerPhoneItemService.deleteLoanerPhoneItem(hubId, id).pipe(
      map(() => {
        dialog.close();
        this.notificationService.success('api.loanerPhone.success.delete');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
      finalize(() => ctx.patchState({ isDeleting: false })),
    );
  }

  @Action(HubLoanerPhoneListInitialState)
  setInitialState(ctx: StateContext<HubLoanerPhoneListStateModel>): void {
    ctx.setState(initialState);
  }
}
